import { useState } from "react";
import { toast } from "sonner";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { Calendar, Send } from "lucide-react";

const EVENT_TYPES = ["Speaking Engagement", "Wedding", "Funeral", "Revival", "Conference", "Counseling", "Other"];

export default function BookPastor() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    event_type: "Speaking Engagement", preferred_date: "", preferred_time: "",
    location: "", expected_audience: "", details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setSubmitted(true);
      toast.success("Booking request submitted — we'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success-page">
        <PageHero
          overline="Thank You"
          title={<>Your request is <em className="text-gold-light italic font-light">received</em>.</>}
          kicker="The Sixth Mount Zion office will review and contact you shortly to confirm Pastor's availability."
        />
      </div>
    );
  }

  return (
    <div data-testid="book-pastor-page">
      <PageHero
        overline="Book the Pastor"
        title={<>Request the <em className="text-gold-light italic font-light">Pastor's</em> presence.</>}
        kicker="For speaking engagements, weddings, funerals, conferences, and more. Submit your request below."
        image="/images/branding/pastor-worship.jpg"
      />

      <Container className="pb-32">
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-8 max-w-4xl" data-testid="pastor-booking-form">
          <Field label="Your Name" required>
            <input data-testid="booking-name" type="text" required value={form.name} onChange={update("name")} className={inputCls} />
          </Field>
          <Field label="Email" required>
            <input data-testid="booking-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
          </Field>
          <Field label="Phone" required>
            <input data-testid="booking-phone" type="tel" required value={form.phone} onChange={update("phone")} className={inputCls} />
          </Field>
          <Field label="Organization">
            <input data-testid="booking-org" type="text" value={form.organization} onChange={update("organization")} className={inputCls} />
          </Field>
          <Field label="Type of Engagement" required>
            <select data-testid="booking-event-type" required value={form.event_type} onChange={update("event_type")} className={inputCls}>
              {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-wine-950">{t}</option>)}
            </select>
          </Field>
          <Field label="Expected Audience Size">
            <input data-testid="booking-audience" type="text" value={form.expected_audience} onChange={update("expected_audience")} className={inputCls} placeholder="e.g. 200" />
          </Field>
          <Field label="Preferred Date" required>
            <input data-testid="booking-date" type="date" required value={form.preferred_date} onChange={update("preferred_date")} className={inputCls} />
          </Field>
          <Field label="Preferred Time" required>
            <input data-testid="booking-time" type="time" required value={form.preferred_time} onChange={update("preferred_time")} className={inputCls} />
          </Field>
          <Field label="Location" className="md:col-span-2">
            <input data-testid="booking-location" type="text" value={form.location} onChange={update("location")} className={inputCls} placeholder="Address or venue" />
          </Field>
          <Field label="Details & Context" required className="md:col-span-2">
            <textarea data-testid="booking-details" required rows={6} value={form.details} onChange={update("details")} className={inputCls} placeholder="Tell us about the event, your audience, and what you're hoping the Pastor will share." />
          </Field>

          <div className="md:col-span-2 flex items-center justify-between pt-6 border-t border-white/10">
            <div className="text-xs text-white/50 italic">We aim to respond within 5–7 business days.</div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="booking-submit-btn"
            >
              <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">{label}{required && <span className="text-gold ml-1">*</span>}</div>
      {children}
    </label>
  );
}
